import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { fetchClients } from "../Slices/clientSlice";
import { fetchDynamicRoutes } from "../Slices/DyanamicRoute";
import { fetchData, fetchNestedData } from "../Slices/dataBucket";
import {
  fetchUserDetails,
  fetchAllUsersThunk,
  createLoginWorkHistoryAsync,
} from "../Slices/Thunk/LoginThunk";
import { fetchLeads, fetchLeadFiltersThunk } from "../Slices/salesSlice";
import { SetNotification } from "../Slices/socketemail";
import { getProgress } from "../Slices/attendenceSlice";
import { fetchEmails } from "../Slices/sharedEmailSlice";
import { fetchEmployeeAccess } from "../Slices/employeeslice";
import { setMailList } from "../Slices/notification";

// Module-level cache — parseJwt is called on every render of the hook;
// memoizing avoids repeated atob/JSON.parse for the same token string.
const jwtCache = new Map();
const parseJwt = (token) => {
  if (!token) return null;
  if (jwtCache.has(token)) return jwtCache.get(token);
  try {
    const result = JSON.parse(atob(token.split(".")[1]));
    jwtCache.set(token, result);
    return result;
  } catch (e) {
    return null;
  }
};

/**
 * useWebtopBootstrap — loads the global app data the rest of the screens depend
 * on (user details, employee access, all users, dynamic routes, databucket,
 * leads, clients, emails, etc.).
 *
 * The legacy app loads this inside AppContainer on mount. The webtop is mounted
 * at /webtop OUTSIDE the tab system, so AppContainer never runs there — meaning
 * a hard reload directly on /webtop would leave every data-driven screen empty
 * ("No data", "undefined Department", "No sales email accounts assigned").
 *
 * Robust guard: the webtop can be reached in several states (fresh reload on
 * /webtop, navigation in from the legacy app, or straight from login before
 * AppContainer's Phase 2 finished). `userStatus` only means "user details
 * loaded" — it does NOT mean the heavy global data (NestedDatabucket, leads,
 * clients, …) is loaded. So gating solely on userStatus (like AppContainer
 * does) would skip the data load in a partial-load state and leave screens
 * empty — e.g. the Department dropdowns (Route Management + the task switcher)
 * both read state.dataBucket.NestedDatabucket. We therefore bootstrap whenever
 * EITHER the user context OR the global bucket data is missing, and run it once
 * per mount (ref-guarded) so the userStatus->true re-render can't double-fetch.
 */
export const useWebtopBootstrap = () => {
  const dispatch = useDispatch();

  const token = localStorage.getItem("token");
  const userId = parseJwt(token)?.id;
  const userStatus = useSelector((state) => state?.loginUserInfo?.userStatus);
  const nestedLoaded = useSelector(
    (state) => (state?.dataBucket?.NestedDatabucket?.length || 0) > 0
  );
  const bootstrappedRef = useRef(false);

  // Restore unread-notification ids from localStorage (mirrors AppContainer).
  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem("uniqueEmailIds") || "[]");
    dispatch(SetNotification(stored));
  }, [dispatch]);

  // Phase 1 (critical) + Phase 2 (background) data load.
  useEffect(() => {
    if (bootstrappedRef.current) return;
    if (!token || !userId) return;
    // Everything already present (came from a fully-loaded legacy session) → no-op.
    if (userStatus && nestedLoaded) return;

    bootstrappedRef.current = true;
    const freshLogin = !userStatus; // only log a "login" work-history entry once

    // NOTE: no cancel-on-unmount guard — this effect only dispatches to the
    // global store (no local setState), so a dispatch after unmount is harmless.
    // A cancel flag previously killed Phase 2 (the userStatus->true re-render
    // cancelled the in-flight run), leaving NestedDatabucket unloaded.
    const fetchAllData = async () => {
      try {
        // Phase 1 — critical: user context, access, routing, databucket.
        // Race a 12s timeout so a hung API call never blocks the rest.
        await Promise.race([
          Promise.all([
            dispatch(fetchUserDetails(userId)),
            dispatch(fetchEmployeeAccess({ userId })),
            dispatch(fetchAllUsersThunk(userId)),
            dispatch(fetchDynamicRoutes()),
            dispatch(fetchData()),
          ]),
          new Promise((resolve) => setTimeout(resolve, 12000)),
        ]);
      } catch (error) {
        console.error("[webtop] Error fetching critical data:", error);
      }

      // Phase 2 — background: heavy data. silent:true keeps the legacy
      // full-screen loader from firing on the webtop surface.
      try {
        await Promise.all([
          dispatch(fetchLeads({ silent: true })),
          dispatch(fetchLeadFiltersThunk({ silent: true })),
          dispatch(fetchClients({ silent: true })),
          dispatch(getProgress({ id: userId })),
          dispatch(fetchNestedData({ silent: true })),
          dispatch(fetchEmails({ silent: true })),
          ...(freshLogin
            ? [
                dispatch(
                  createLoginWorkHistoryAsync({
                    userId,
                    title: "",
                    description: "",
                    companyname: "BlueplanIt",
                    department: "",
                    task: "login",
                    date: "",
                    startTime: new Date().toISOString(),
                    endTime: "",
                  })
                ),
              ]
            : []),
        ]);
        console.log("[webtop] bootstrap complete (NestedDatabucket + global data loaded)");
      } catch (error) {
        console.error("[webtop] Error fetching background data:", error);
      }
    };

    console.log(
      `[webtop] bootstrap starting (userStatus=${userStatus}, nestedLoaded=${nestedLoaded}, freshLogin=${freshLogin})`
    );
    fetchAllData();
  }, [dispatch, userId, userStatus, token, nestedLoaded]);

  // Build the per-user mail list (drives Sales Outlook account list) once user
  // details are loaded — mirrors AppContainer's EmpMails → setMailList effect.
  const loginData = useSelector(
    (state) => state?.loginUserInfo?.loginUserDetails
  );
  const personalMail = loginData?.email;
  const mails =
    loginData?.emailSettings
      ?.filter((item) => item.status === true || item.status === "true")
      ?.map((item) => item.email) || [];
  const salesMails =
    loginData?.salesEmailSettings
      ?.filter((item) => item.status === true || item.status === "true")
      ?.map((item) => item.email)
      ?.filter(Boolean) || [];
  const EmpMails = Array.from(
    new Set([...mails, ...salesMails, personalMail].filter(Boolean))
  );

  useEffect(() => {
    if (EmpMails?.length > 0) dispatch(setMailList(EmpMails));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, EmpMails.join(",")]);
};

export default useWebtopBootstrap;
